import type { Vue } from "vue-property-decorator";
import { useAppStore } from "@/store/app-store";

// .ts から App.vueのイベントモーダルへの橋渡しの役割を担う

const EVENT_CT: number = 5000; // イベントのクールタイム

/** イベント管理クラス */
export class EventManager {
  private static $root?: Vue;
  private static events: { [key: string]: { callable: boolean } } = {};

  public static init($root: Vue): void {
    EventManager.$root = $root;
  }

  private static executable(key: string): boolean {
    if (!EventManager.events[key]) {
      EventManager.events[key] = { callable: true };
    }
    return EventManager.events[key].callable;
  }

  private static restrictEvent(key: string) {
    if (!EventManager.events[key]) {
      EventManager.events[key] = { callable: false };
    } else {
      EventManager.events[key].callable = false;
    }

    const refresh = () => {
      EventManager.events[key].callable = true;
    };
    setTimeout(refresh, EVENT_CT);
  }

  // 無料版の制限に関するアラート
  public static freeAlertEvent(): void {
    EventManager.$root?.$emit("free-alert");
  }

  // 強制的にログアウトに遷移させるイベント
  public static logoutEvent(): void {
    const eventKey = "logout";
    if (!EventManager.executable(eventKey)) {
      return;
    }
    const escapePassList = ["/logout", "/login"];
    const isEscaped = escapePassList.some((pass) =>
      EventManager.$root?.$route.path.match(pass),
    );
    if (!isEscaped) {
      EventManager.$root?.$router.replace("/logout");
      EventManager.restrictEvent(eventKey);
    }
  }

  // アクセス権がない時のイベント
  public static accessDeinedAlert(msg: string): void {
    let args = {
      msg: "アクセス権がありません。",
      afterProcess: () => {
        return;
      },
    };
    // 話題が取得できない
    if (msg.indexOf("topic") !== -1) {
      const domainId = useAppStore().domainId;
      args = {
        msg: "こちらの話題は削除されたか、もしくは閲覧権限がありません。",
        afterProcess: () => {
          EventManager.$root?.$router.push(`/${domainId}`);
        },
      };
      // 投稿が取得できない
    } else if (msg.indexOf("message") !== -1) {
      const domainId = useAppStore().domainId;
      const topicId = useAppStore().topicId;
      args = {
        msg: "こちらの投稿は削除されたか、もしくは閲覧権限がありません。",
        afterProcess: () => {
          EventManager.$root?.$router.push(`/${domainId}/${topicId}`);
        },
      };
      // default
    } else {
      args = {
        msg: "アクセス権がありません。",
        afterProcess: () => {
          return;
        },
      };
    }
    EventManager.$root?.$emit("show-error-modal", args);
  }

  // not foundの時のイベント
  // (現在は削除も404エラーになる)
  public static notFoundAlert(msg: string): void {
    let args = {
      msg: "取得に失敗しました",
      afterProcess: () => {
        return;
      },
    };
    // 話題が取得できない
    if (msg.indexOf("topic") !== -1) {
      const domainId = useAppStore().domainId;
      args = {
        msg: "こちらの話題は削除されたか、もしくは閲覧権限がありません。",
        afterProcess: () => {
          EventManager.$root?.$router.push(`/${domainId}`);
        },
      };
      // 投稿が取得できない
    } else if (msg.indexOf("message") !== -1) {
      const domainId = useAppStore().domainId;
      const topicId = useAppStore().topicId;
      args = {
        msg: "こちらの投稿は削除されたか、もしくは閲覧権限がありません。",
        afterProcess: () => {
          EventManager.$root?.$router.push(`/${domainId}/${topicId}`);
        },
      };
      // default
    } else {
      args = {
        msg: "取得に失敗しました",
        afterProcess: () => {
          return;
        },
      };
    }
    EventManager.$root?.$emit("show-error-modal", args);
  }

  // ネットワークエラー（429 / 500）
  public static networkErrorAlert(): void {
    const eventKey = "networkError";
    if (!EventManager.executable(eventKey)) {
      return;
    }
    const args = {
      msg: "ネットワークエラーが発生しました。\n時間をおいてお試しください",
      afterProcess: () => {
        return;
      },
    };
    EventManager.$root?.$emit("show-error-modal", args);
    EventManager.restrictEvent(eventKey);
  }

  // 有効な組織かどうかのイベント
  public static allowedDomainEvent(show: boolean): void {
    EventManager.$root?.$emit("on-not-allowed-modal", { show });
  }

  /** 再ログインに流す */
  public static relogin(): void {
    EventManager.$root?.$emit("on-relogin");
  }
}
